import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LayoutDashboard, Users, ScrollText, Settings, Building2, BarChart3, IdCard, UserCheck, ShieldAlert, ListChecks, Lock, Activity, Inbox, Plug } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider } from "./context/AuthContext";
import { RequireAuth } from "./components/RequireAuth";
import { PanelLayout } from "./components/PanelLayout";
import { AuditView } from "./components/AuditView";
import { SettingsView } from "./components/SettingsView";
import { PageHeader } from "./components/PageHeader";
import Landing from "./pages/Landing";
import Registro from "./pages/Registro";
import Ingresar from "./pages/Ingresar";
import ReinicioSuperadmin from "./pages/ReinicioSuperadmin";
import Invitacion from "./pages/Invitacion";
import KycPage from "./pages/kyc/KycPage";
import Privacidad from "./pages/Privacidad";
import Verificaciones from "./pages/panel/Verificaciones";
import Presencial from "./pages/panel/Presencial";
import Resumen from "./pages/panel/Resumen";
import Usuarios from "./pages/panel/Usuarios";
import Configuracion from "./pages/panel/Configuracion";
import IntegracionApi from "./pages/panel/IntegracionApi";
import Empresas from "./pages/admin/Empresas";
import Metricas from "./pages/admin/Metricas";
import Listas from "./pages/admin/Listas";
import Casos from "./pages/panel/Casos";
import Clientes from "./pages/panel/Clientes";
import ClienteFicha from "./pages/panel/ClienteFicha";
import PrivacidadPanel from "./pages/panel/PrivacidadPanel";
import Uso from "./pages/admin/Uso";
import Demos from "./pages/admin/Demos";
import { LegalRevision } from "./pages/admin/LegalRevision";
import Terminos from "./pages/Terminos";
import DerechosTitular from "./pages/kyc/DerechosTitular";

const MANAGERS = ["owner", "compliance_officer"];
const PANEL_NAV = [
  { id: "resumen", to: "/panel", end: true, label: "Resumen", icon: LayoutDashboard },
  { id: "verificaciones", to: "/panel/verificaciones", label: "Verificaciones", icon: IdCard },
  { id: "clientes", to: "/panel/clientes", label: "Mis clientes", icon: Users },
  { id: "presencial", to: "/panel/presencial", label: "KYC presencial", icon: UserCheck },
  { id: "casos", to: "/panel/casos", label: "Casos", icon: ShieldAlert, roles: MANAGERS },
  { id: "privacidad", to: "/panel/privacidad", label: "Privacidad", icon: Lock, roles: MANAGERS },
  { id: "usuarios", to: "/panel/usuarios", label: "Usuarios", icon: Users, roles: MANAGERS },
  { id: "bitacora", to: "/panel/bitacora", label: "Bitácora", icon: ScrollText, roles: MANAGERS },
  { id: "configuracion", to: "/panel/configuracion", label: "Configuración", icon: Settings, roles: MANAGERS },
  { id: "integracion", to: "/panel/integracion", label: "Integración API", icon: Plug, roles: ["owner"] },
];
const ADMIN_NAV = [
  { id: "empresas", to: "/admin", end: true, label: "Empresas", icon: Building2 },
  { id: "metricas", to: "/admin/metricas", label: "Métricas", icon: BarChart3 },
  { id: "listas", to: "/admin/listas", label: "Listas ONU/OFAC/PEP", icon: ListChecks },
  { id: "uso", to: "/admin/uso", label: "Uso por empresa", icon: Activity },
  { id: "demos", to: "/admin/demos", label: "Solicitudes de demo", icon: Inbox },
  { id: "bitacora", to: "/admin/bitacora", label: "Bitácora global", icon: ScrollText },
  { id: "configuracion", to: "/admin/configuracion", label: "Configuración global", icon: Settings },
];

const AdminSettings = () => (
  <div className="space-y-8">
    <PageHeader overline="Superadmin" title="Configuración global" />
    <LegalRevision />
    <SettingsView base="/admin" canEdit />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/ingresar" element={<Ingresar />} />
          <Route path="/restablecer-superadmin" element={<ReinicioSuperadmin />} />
          <Route path="/invitacion/:token" element={<Invitacion />} />
          <Route path="/k/:slug" element={<KycPage />} />
          <Route path="/k/:slug/derechos" element={<DerechosTitular />} />
          <Route path="/k/:slug/didit" element={<KycPage />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/panel" element={<RequireAuth area="panel"><PanelLayout nav={PANEL_NAV} subtitle="Panel empresa" /></RequireAuth>}>
            <Route index element={<Resumen />} />
            <Route path="verificaciones" element={<Verificaciones />} />
            <Route path="presencial" element={<Presencial />} />
            <Route path="casos" element={<Casos />} />
            <Route path="clientes" element={<Clientes />} />
            <Route path="clientes/:id" element={<ClienteFicha />} />
            <Route path="privacidad" element={<PrivacidadPanel />} />
            <Route path="usuarios" element={<Usuarios />} />
            <Route path="bitacora" element={<AuditView base="/company" title="Bitácora de la empresa" />} />
            <Route path="configuracion" element={<Configuracion />} />
            <Route path="integracion" element={<IntegracionApi />} />
          </Route>
          <Route path="/admin" element={<RequireAuth area="admin"><PanelLayout nav={ADMIN_NAV} subtitle="Superadmin" /></RequireAuth>}>
            <Route index element={<Empresas />} />
            <Route path="metricas" element={<Metricas />} />
            <Route path="listas" element={<Listas />} />
            <Route path="uso" element={<Uso />} />
            <Route path="demos" element={<Demos />} />
            <Route path="bitacora" element={<AuditView base="/admin" title="Bitácora global" />} />
            <Route path="configuracion" element={<AdminSettings />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}

export default App;
